import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useClientAuth } from "../hooks/useClientAuth";

/**
 * Route guard for the client portal — redirects to login when the visitor
 * isn't authenticated, preserving the requested URL in state so we can send
 * them back after they sign in.
 *
 * Session restore is async (Supabase reads it on mount), so we wait on
 * `loading` before deciding — otherwise a logged-in client gets bounced to
 * /login on every hard refresh.
 */
interface ClientGuardProps {
  children: React.ReactNode;
}

export const ClientGuard = ({ children }: ClientGuardProps) => {
  const { isAuthenticated, loading } = useClientAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/client/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
};

export default ClientGuard;
